import { Component, OnInit } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { PeerReviewService } from '../../services/peer-review/peer-review.service';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { PeerReviewerResponse } from '../../models/super-admin/peerReviewerResponse';
import { CommonModule } from '@angular/common';
import { SkeletonModule } from 'primeng/skeleton';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { ContactUsService } from '../../services/website/contact-us.service';
import { MessageService } from 'primeng/api';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-peer-profile',
  standalone: true,
  imports: [ButtonModule, CommonModule, RouterModule, SkeletonModule, FormsModule],
  templateUrl: './peer-profile.component.html',
  styleUrl: './peer-profile.component.scss',
  providers: [MessageService]
})
export class PeerProfileComponent implements OnInit {
  peerId: string | undefined;
  peer: PeerReviewerResponse | undefined;
  similarPeers: PeerReviewerResponse[] = [];
  isLoadingPeer: boolean = false;
  isLoadingSimilar: boolean = false;

  // Enquiry Modal Properties
  showEnquiryModal: boolean = false;
  enquiryQuery: string = '';
  currentUser: any;
  isSendingEnquiry: boolean = false;

  constructor(
    private peerReviewService: PeerReviewService,
    private route: ActivatedRoute,
    private store: Store,
    private contactUsService: ContactUsService,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.peerId = params.get('id') || '';
      this.getPeerReviwer();
      this.getSimilarPeers();
    });

    // Subscribe to current logged-in user state
    this.store.select(UserSelectors.getUser).subscribe({
      next: user => {
        this.currentUser = user;
      },
      error: err => {
        console.error('Error fetching current user state:', err);
      }
    });
  }

  getPeerReviwer() {
    this.isLoadingPeer = true;
    this.peerReviewService.getPeerReviewerById(this.peerId).subscribe({
      next: response => {
        this.peer = response;
        this.isLoadingPeer = false;
      },
      error: err => {
        console.error(err);
        this.isLoadingPeer = false;
      }
    });
  }

  getSimilarPeers() {
    this.isLoadingSimilar = true;
    this.peerReviewService.getAllPeerReviewers({
      pageNumber: 1,
      pageSize: 10
    }).subscribe({
      next: response => {
        if (response && response.result) {
          // Filter out currently viewed peer and take top 3
          this.similarPeers = response.result
            .filter(p => p.applicationUserId !== this.peerId)
            .slice(0, 3);
        } else {
          this.similarPeers = [];
        }
        this.isLoadingSimilar = false;
      },
      error: err => {
        console.error('Error fetching similar peers:', err);
        this.isLoadingSimilar = false;
      }
    });
  }

  // Enquiry Modal Handlers
  openEnquiryModal(event?: Event) {
    if (event) {
      event.preventDefault();
    }
    this.showEnquiryModal = true;
  }

  closeEnquiryModal() {
    this.showEnquiryModal = false;
    this.enquiryQuery = '';
  }

  sendEnquiry() {
    if (!this.enquiryQuery.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please enter your query first.'
      });
      return;
    }

    this.isSendingEnquiry = true;
    this.contactUsService.submitContactUsForm({
      contactPurpose: 6, // FreeConsultation (corresponds to ContactPurpose enum)
      firstName: this.currentUser?.firstName || 'Logged-In',
      lastName: this.currentUser?.lastName || 'Student',
      email: this.currentUser?.email || 'student@gradpilots.com',
      phoneNumber: this.currentUser?.phoneNumber || '0000000000',
      message: this.enquiryQuery
    }).subscribe({
      next: response => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Your query has been successfully submitted! We will contact you soon.'
        });
        this.isSendingEnquiry = false;
        this.closeEnquiryModal();
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Something went wrong! Please try again.'
        });
        this.isSendingEnquiry = false;
      }
    });
  }

  get peerInitials(): string {
    if (!this.peer) return 'Peer';
    const first = this.peer.firstName ? this.peer.firstName.trim().charAt(0) : '';
    const last = this.peer.lastName ? this.peer.lastName.trim().charAt(0) : '';
    return (first + last).toUpperCase() || 'Peer';
  }

  get peerInitialsDot(): string {
    if (!this.peer) return 'Peer';
    return this.getInitialsDot(this.peer.firstName, this.peer.lastName);
  }

  getInitialsDot(firstName?: string | null, lastName?: string | null): string {
    const first = firstName ? firstName.trim().charAt(0) : '';
    const last = lastName ? lastName.trim().charAt(0) : '';
    if (first && last) return `${first.toUpperCase()}.${last.toUpperCase()}.`;
    if (first) return `${first.toUpperCase()}.`;
    return 'Peer';
  }

  get extracurricularList(): string[] {
    if (!this.peer?.extraCurricularActivitiesDescription) return [];
    return this.peer.extraCurricularActivitiesDescription
      .split('\n')
      .map(item => item.replace(/^[-*•\s]+/, '').trim())
      .filter(item => item.length > 0);
  }

  getCountryCode(countryName: string | null | undefined): string {
    if (!countryName) return '';
    const nameLower = countryName.toLowerCase().trim();
    const map: { [key: string]: string } = {
      'india': 'IN',
      'united states': 'US',
      'usa': 'US',
      'us': 'US',
      'united kingdom': 'GB',
      'uk': 'GB',
      'gb': 'GB',
      'canada': 'CA',
      'australia': 'AU',
      'germany': 'DE',
      'france': 'FR',
      'singapore': 'SG',
      'united arab emirates': 'AE',
      'uae': 'AE'
    };
    return map[nameLower] || '';
  }
}


